/**
 * OnlinePaymentStatus - the customer's "pay and wait" screen for an online
 * order, shared by online.chain29.com and chain29.com (docs/CHECKOUT_API_2026-10-28.md §6).
 *
 * - The payment page opens in the SAME tab; the gateway returns the customer to
 *   chain29.com/order/status/<id>?t= (the status page shows this screen until paid);
 *   /order/checkout?order=&t= resumes from the URL alone too.
 * - Amounts are the server's (subtotal / discount / tax / tip / total / paid from the poll),
 *   so a return on another device shows them too.
 * - A QR of the page for paying on a phone: the Hub's own image verbatim when it
 *   sent one, else a QR of pay_url.
 * - Words per state: declined / cancelled = not charged; timed out / error /
 *   no answer yet = never "not charged" (a late approval is still confirmed).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { AlertCircle, Clock, CreditCard, Loader2, Lock, RefreshCw, ShieldCheck, Smartphone, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { hubQrImage, payAgain, rememberedOrder, useOnlinePayment } from './onlineCheckout';

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const errText = (error, fallback) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

/** The server's amounts (GET /online/orders/{id}/payment) - also on another device / after a reload. */
function Amounts({ saved }) {
  if (saved?.total_cents == null) return null;
  const rows = [
    ['Subtotal', saved.subtotal_cents],
    saved.discount_cents ? ['Discount', -saved.discount_cents] : null,
    ['Tax', saved.tax_cents],
    saved.tip_cents ? ['Tip', saved.tip_cents] : null,
  ].filter(Boolean);
  const paid = saved.paid_cents || 0;
  return (
    <div className="rounded-xl bg-muted/50 px-4 py-3 text-sm space-y-1.5 text-left" data-testid="online-server-totals">
      {rows.map(([label, cents]) => (
        <div key={label} className="flex justify-between text-muted-foreground">
          <span>{label}</span><span>{cents < 0 ? `-${money(-cents)}` : money(cents)}</span>
        </div>
      ))}
      <div className="flex justify-between font-semibold text-foreground pt-1.5 border-t border-border">
        <span>Total</span><span data-testid="online-total">{money(saved.total_cents)}</span>
      </div>
      {paid > 0 && (
        <div className="flex justify-between text-muted-foreground" data-testid="online-paid">
          <span>Paid</span><span>{money(paid)}</span>
        </div>
      )}
    </div>
  );
}

export default function OnlinePaymentStatus({ orderId, accessToken, restaurantName, cancelledReturn = false, onPaid }) {
  const { state, data, confirming, netError, checkAgain } = useOnlinePayment(orderId, accessToken, { onPaid });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(() => rememberedOrder(orderId));
  const [showQr, setShowQr] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);

  useEffect(() => { setSaved(rememberedOrder(orderId)); }, [orderId, data]);
  // the server's figures win; the remembered answer only fills in before the first poll
  const amounts = data?.total_cents != null ? data : saved;

  const page = saved?.payment;
  const payUrl = data ? data.pay_url : page?.pay_url;
  const qrImage = useMemo(() => (page?.pay_url && page.pay_url === payUrl ? hubQrImage(page) : null), [page, payUrl]);

  const openPage = (url) => { window.location.assign(url); };

  const handlePayAgain = async () => {
    setBusy(true);
    try {
      const res = await payAgain(orderId, accessToken);
      if (res.payment_status === 'paid') {
        checkAgain();
        return;
      }
      const url = res.payment?.pay_url;
      if (url) { openPage(url); return; }
      checkAgain();
    } catch (error) {
      toast.error(errText(error, 'Could not open a new payment page. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const shell = (testId, children) => (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4" data-testid="online-payment-screen">
      <Card className="w-full max-w-md shadow-xl border-border/60" data-testid={testId} data-state={state}>
        <CardContent className="p-6 sm:p-8 text-center space-y-5">{children}</CardContent>
      </Card>
    </div>
  );

  const header = (Icon, tone, title, text) => (
    <>
      <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${tone}`}>
        <Icon className="h-8 w-8" />
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl font-bold tracking-tight" data-testid="online-payment-title">{title}</h2>
        {text && <p className="text-muted-foreground leading-relaxed" data-testid="online-payment-text">{text}</p>}
      </div>
    </>
  );

  const payAgainButton = (label = 'Pay now') => (
    <Button onClick={handlePayAgain} disabled={busy} className="w-full h-12 text-base font-semibold" data-testid="pay-again-btn">
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2" />{label}</>}
    </Button>
  );

  if (state === 'loading' || state === 'idle' || state === 'paid') {
    return shell('online-pay-loading', header(Loader2, 'bg-primary/10 text-primary [&>svg]:animate-spin',
      state === 'paid' ? 'Payment received' : 'Checking your payment…', state === 'paid' ? 'Opening your order…' : null));
  }

  if (state === 'not_found') {
    return shell('online-pay-not-found', header(AlertCircle, 'bg-red-100 text-red-600', 'We couldn\'t find this order',
      'The link may be incomplete. If you were charged, the restaurant will confirm your order.'));
  }

  if (state === 'order_cancelled') {
    return shell('online-pay-order-cancelled', header(XCircle, 'bg-red-100 text-red-600', 'This order was cancelled',
      'It was not sent to the kitchen. If you see a charge, the restaurant will confirm or refund it.'));
  }

  if (state === 'declined' || state === 'cancelled' || state === 'timed_out' || state === 'error') {
    const words = {
      declined: ['Your card was declined', `${data?.message && !/declin/i.test(data.message) ? `${data.message}. ` : ''}You were not charged. Your order is saved — try again with the same or another card.`],
      cancelled: ['Payment cancelled', 'You were not charged. Your order is saved — pay whenever you are ready.'],
      timed_out: ['The payment page expired', 'No payment was confirmed before the page expired. If you did pay, we will still confirm it here — paying again first checks the last page.'],
      error: ['The payment did not complete', 'If you see a charge on your card, the restaurant will confirm it. Otherwise you can try again.'],
    }[state];
    const Icon = state === 'declined' ? XCircle : state === 'timed_out' ? Clock : AlertCircle;
    return shell('online-pay-failed', <>
      {header(Icon, state === 'timed_out' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600', words[0], words[1])}
      <Amounts saved={amounts} />
      {data?.can_pay_again !== false ? payAgainButton(state === 'cancelled' ? 'Pay now' : 'Try payment again')
        : <p className="text-sm text-muted-foreground">Please contact the restaurant about this order.</p>}
    </>);
  }

  // waiting: the page is open (or the gateway has not answered yet)
  return shell('online-pay-waiting', <>
    {header(confirming ? Clock : ShieldCheck, 'bg-primary/10 text-primary',
      confirming ? 'We\'re confirming your payment'
        : (amounts?.total_cents != null ? `Pay ${money(Math.max(amounts.total_cents - (amounts.paid_cents || 0), 0))}` : 'Complete your payment'),
      confirming ? 'If you were charged, the restaurant will confirm your order. You can close this page or check again.'
        : `Pay on ${restaurantName ? `${restaurantName}'s` : 'the restaurant\'s'} secure Aster Gateway page. You'll come right back here.`)}

    {cancelledReturn && !confirming && (
      <p className="text-sm rounded-lg bg-amber-50 text-amber-800 px-3 py-2" data-testid="online-returned-cancelled">
        You left the payment page. Your order is saved — it is sent to the kitchen once it is paid.
      </p>
    )}

    <Amounts saved={amounts} />

    {payUrl ? (
      <a href={payUrl}
        className="flex items-center justify-center gap-2 w-full h-12 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-colors"
        data-testid="open-payment-page">
        <Lock className="h-4 w-4" /> {cancelledReturn || confirming ? 'Return to the payment page' : 'Continue to secure payment'}
      </a>
    ) : (
      !confirming && <p className="text-sm text-muted-foreground">Waiting for the payment page…</p>
    )}

    {payUrl && (
      <div className="pt-1">
        {showQr ? (
          <div className="flex flex-col items-center gap-2" data-testid="online-pay-qr">
            <div className="bg-white p-3 rounded-xl border">
              {qrImage
                ? <img src={qrImage} alt="Scan to pay" className="w-44 h-44" data-testid="online-pay-qr-image" />
                : <QRCodeSVG value={payUrl} size={176} level="M" data-testid="online-pay-qr-svg" />}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Smartphone className="h-3.5 w-3.5" /> Or scan to pay on your phone</p>
          </div>
        ) : (
          <button type="button" onClick={() => setShowQr(true)} className="text-sm text-muted-foreground underline underline-offset-4"
            data-testid="online-show-qr">
            Pay on another phone (QR)
          </button>
        )}
      </div>
    )}

    {confirming ? (
      <Button variant="outline" onClick={checkAgain} className="w-full h-11" data-testid="online-check-again">
        <RefreshCw className="h-4 w-4 mr-2" /> Check again
      </Button>
    ) : (
      <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="online-waiting">
        <Loader2 className="h-4 w-4 animate-spin" /> {netError ? 'Reconnecting…' : 'Waiting for your payment…'}
      </p>
    )}
  </>);
}
